// import: styles
import { Avatar, Box, useMediaQuery } from '@mui/material';
import Popover from '@mui/material/Popover';

// import: assets
import { ReactComponent as Account } from 'core/assets/svg/app-bar/popover/account.svg';
import { ReactComponent as ApiIcon } from 'core/assets/svg/app-bar/popover/api-icon.svg';
import { ReactComponent as Credits } from 'core/assets/svg/app-bar/popover/credits.svg';
import { ReactComponent as DefaultPfp } from 'core/assets/svg/app-bar/popover/default_pfp.svg';
import { ReactComponent as Logout } from 'core/assets/svg/app-bar/popover/logout.svg';
import BaseButton from 'core/components/button/BaseButton';
import Linked from 'core/components/link/Link';
import BaseTypography from 'core/components/typography/BaseTypography';
import { LogoutEnum } from 'core/constants/enums/LogoutEnum.ts';

// import enums
import { ProfilePopOverOptions } from 'core/constants/enums/ProfilePopOverOptions.ts';
import ProfileButtonDrawer from 'core/layouts/components/profile-button/component/ProfileButtonDrawer';
import { profileButtonStyles } from 'core/layouts/components/profile-button/ProfileButtonStyles';
// import: components
import ProfileCard from 'core/layouts/components/profile-card/ProfileCard';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
// import: constants
import { ACCOUNT, API, LOGIN, PRICING } from 'core/navigation/Routes';

// import: services
// import: utils
// import: redux
import {
	currentUserSelector,
	serverWorkingSelector,
	userBlockedSelector,
	userProfileSelector,
} from 'core/store/reducers/appReducer/AppSlice';
import { googleSignOut } from 'core/store/reducers/appReducer/appSliceThunk';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// import: lazy load components

function ProfileButton() {
	const dispatch = useDispatch();
	const isSmallScreen = useMediaQuery('(max-width:1300px)');

	const serverWorking = useSelector(serverWorkingSelector);
	const userBlocked = useSelector(userBlockedSelector);

	const currentUser = useSelector(currentUserSelector);

	const userProfile = useSelector(userProfileSelector);

	const [anchorEl, setAnchorEl] = React.useState(null);
	const [openDrawer, setOpenDrawer] = useState(false);

	const handleClick = (event) => {
		setOpenDrawer((prev) => !prev);
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
		setOpenDrawer(false);
	};

	const open = Boolean(anchorEl);
	const id = open ? 'profile-button-popover' : undefined;

	const popOverOptions = useMemo(
		() => [
			{
				id: ProfilePopOverOptions.ACCOUNT,
				icon: <Account />,
				label: 'Account',
				isVisible: !window.electron,
				to: `/${ACCOUNT}`,
				onClickFunc: (e) => e.stopPropagation(),
			},
			{
				id: ProfilePopOverOptions.CREDITS,
				icon: <Credits />,
				label: 'Pricing',
				isVisible: !window.electron,
				to: `/${PRICING}`,
				onClickFunc: (e) => e.stopPropagation(),
			},
			{
				id: ProfilePopOverOptions.API,
				icon: <ApiIcon />,
				label: 'Affogato API',
				isVisible: !window.electron,
				to: `/${API}`,
				onClickFunc: (e) => e.stopPropagation(),
			},
			// might add later
			// {
			// 	id: ProfilePopOverOptions.CONTACT,
			// 	icon: <ContactSupport />,
			// 	label: 'Expert prompt assist',
			// 	isVisible: userProfile.supportEnabled && !window.electron,
			// 	onClickFunc: (e) => {
			// 		e.stopPropagation();
			// 		window.Intercom('boot', {
			// 			api_base: 'https://api-iam.intercom.io',
			// 			app_id: intercomAppId,
			// 			name: userProfile?.name,
			// 			email: userProfile.email,
			// 			user_type: userProfile?.currentPlan,
			// 		});
			// 		window.Intercom('show');
			// 	},
			// },
			{
				id: ProfilePopOverOptions.LOGOUT,
				icon: <Logout />,
				label: 'Logout',
				isVisible: true,
				to: `/${LOGIN}`,
				onClickFunc: (e) => {
					e.stopPropagation();
					dispatch(googleSignOut(LogoutEnum.USER_LOGGED_OUT));
					window.Intercom('shutdown');
				},
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
		],
		[dispatch],
	);

	return serverWorking && !userBlocked
		? currentUser && (
				<>
					<BaseButton
						id="profile-button"
						aria-describedby={id}
						styles={[profileButtonStyles.profileButton]}
						onClick={handleClick}
					>
						<Box sx={profileButtonStyles.avatarCont}>
							<Avatar
								src={userProfile.profileUrl}
								alt="Profile avatar"
								sx={profileButtonStyles.avatar}
							>
								<DefaultPfp />
							</Avatar>
						</Box>
					</BaseButton>

					{(!isSmallScreen || window.electron) && (
						<Popover
							id={id}
							open={open}
							anchorEl={anchorEl}
							onClose={handleClose}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'right',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'right',
							}}
							sx={[profileButtonStyles.popover, { top: '12px' }]}
						>
							<Box id="popover-container" sx={profileButtonStyles.popoverContainer}>
								<Box sx={[profileButtonStyles.profileDetails]}>
									<ProfileCard />
								</Box>
								<Box sx={[profileButtonStyles.popoverDivider]} />
								{popOverOptions.map((option) =>
									option.isVisible ? (
										<React.Fragment key={`option-${option.label}`}>
											{option.id !== ProfilePopOverOptions.CONTACT ? (
												<Linked key={`option-${option.label}`} to={option.to}>
													<Box
														key={`option-${option.label}`}
														sx={[profileButtonStyles.popoverOption, { cursor: 'pointer' }]}
														onClick={(e) => {
															option.onClickFunc(e);
															handleClose();
														}}
													>
														<Box sx={[reusableStyles.flexRow, { gap: '16px' }]}>
															{option.icon}
															<BaseTypography
																variant="b1_regular"
																styles={{ color: 'colors.light2' }}
															>
																{option.label}
															</BaseTypography>
														</Box>
													</Box>
												</Linked>
											) : (
												<Box
													key={`option-${option.label}`}
													sx={[profileButtonStyles.popoverOption, { cursor: 'pointer' }]}
													onClick={(e) => {
														option.onClickFunc(e);
														handleClose();
													}}
												>
													<Box sx={[reusableStyles.flexRow, { gap: '16px' }]}>
														{option.icon}
														<BaseTypography
															variant="b1_regular"
															styles={{ color: 'colors.light2' }}
														>
															{option.label}
														</BaseTypography>
													</Box>
												</Box>
											)}
										</React.Fragment>
									) : null,
								)}
							</Box>
						</Popover>
					)}

					{isSmallScreen && !window.electron && (
						<ProfileButtonDrawer openDrawer={openDrawer} setOpenDrawer={setOpenDrawer} />
					)}
				</>
			)
		: null;
}

export default ProfileButton;
